<template>
  <div class="qbank-leaderboard">
    <!-- Header -->
    <div class="leaderboard-header">
      <button class="back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="header-content">
        <div class="header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="12" width="5" height="9" rx="1"/>
            <rect x="10" y="5" width="5" height="16" rx="1"/>
            <rect x="17" y="9" width="5" height="12" rx="1"/>
          </svg>
        </div>
        <div class="header-text">
          <h1>{{ $t("qbank.leaderboard") || "المتصدرين" }}</h1>
          <p>{{ $t("qbank.leaderboard_desc") || "أفضل الطلاب في بنك الأسئلة" }}</p>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div v-if="children && children.length > 0" class="filter-section">
      <div class="filter-header">
        <i class="fas fa-filter"></i>
        <span>{{ $t("qbank.filter_by") || "تصفية حسب" }}</span>
      </div>
      <div class="filter-options">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': selectedFilter === '' }"
          @click="applyFilter('')"
        >
          <i class="fas fa-globe"></i>
          {{ $t("qbank.all") || "الكل" }}
        </button>
        <button
          v-for="child in children"
          :key="child.id"
          class="filter-chip"
          :class="{ 'filter-chip--active': selectedFilter === child.id }"
          @click="applyFilter(child.id)"
        >
          {{ child.name }}
        </button>
      </div>
    </div>

    <!-- User Rank Card -->
    <div v-if="userRank && userPoints" class="user-rank-card">
      <div class="user-rank-info">
        <div class="user-rank-icon">
          <i class="fas fa-user"></i>
        </div>
        <div class="user-rank-details">
          <span class="user-rank-label">{{ $t("qbank.your_rank") || "ترتيبك" }}</span>
          <span class="user-rank-value">#{{ userRank }}</span>
        </div>
      </div>
      <div class="user-points-info">
        <span class="user-points-value">{{ userPoints }}</span>
        <span class="user-points-label">{{ $t("qbank.points") || "نقطة" }}</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
      </div>
      <p>{{ $t("common.loading") }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <h4>{{ $t("common.error") }}</h4>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="fetchLeaderboard">
        <i class="fas fa-redo"></i>
        {{ $t("common.retry") || "إعادة المحاولة" }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!leaders || leaders.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="12" width="5" height="9" rx="1"/>
          <rect x="10" y="5" width="5" height="16" rx="1"/>
          <rect x="17" y="9" width="5" height="12" rx="1"/>
        </svg>
      </div>
      <h4>{{ $t("qbank.no_leaderboard") || "لا توجد بيانات" }}</h4>
      <p>{{ $t("qbank.no_leaderboard_desc") || "لم يتم تسجيل أي نتائج بعد" }}</p>
    </div>

    <!-- Leaderboard Content -->
    <div v-else class="leaderboard-content">
      <!-- Top 3 Podium -->
      <div v-if="leaders.length >= 3" class="podium-section">
        <!-- Second Place -->
        <div class="podium-item podium-second">
          <div class="podium-avatar">
            <img :src="getAvatar(leaders[1])" @error="onImgError" alt="" />
            <div class="podium-rank">2</div>
          </div>
          <div class="podium-name">{{ leaders[1].name }}</div>
          <div class="podium-score">{{ leaders[1].total_points }} {{ $t("qbank.points") || "نقطة" }}</div>
          <div class="podium-bar second"></div>
        </div>

        <!-- First Place -->
        <div class="podium-item podium-first">
          <div class="crown-icon">👑</div>
          <div class="podium-avatar">
            <img :src="getAvatar(leaders[0])" @error="onImgError" alt="" />
            <div class="podium-rank">1</div>
          </div>
          <div class="podium-name">{{ leaders[0].name }}</div>
          <div class="podium-score">{{ leaders[0].total_points }} {{ $t("qbank.points") || "نقطة" }}</div>
          <div class="podium-bar first"></div>
        </div>

        <!-- Third Place -->
        <div class="podium-item podium-third">
          <div class="podium-avatar">
            <img :src="getAvatar(leaders[2])" @error="onImgError" alt="" />
            <div class="podium-rank">3</div>
          </div>
          <div class="podium-name">{{ leaders[2].name }}</div>
          <div class="podium-score">{{ leaders[2].total_points }} {{ $t("qbank.points") || "نقطة" }}</div>
          <div class="podium-bar third"></div>
        </div>
      </div>

      <!-- Rest of Leaderboard -->
      <div class="leaderboard-list">
        <div
          v-for="user in leadersRest"
          :key="user.user_rank"
          class="leaderboard-item"
        >
          <div class="item-rank">{{ user.user_rank }}</div>
          <div class="item-avatar">
            <img :src="getAvatar(user)" @error="onImgError" alt="" />
          </div>
          <div class="item-info">
            <div class="item-name">{{ user.name }}</div>
          </div>
          <div class="item-score">{{ user.total_points }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "QBankLeaderboard",
  data() {
    return {
      loading: true,
      error: null,
      leaders: [],
      children: [],
      filterKey: null,
      userPoints: null,
      userRank: null,
      selectedFilter: "",
      token: localStorage.getItem("access_token"),
    };
  },
  computed: {
    leadersRest() {
      return this.leaders.slice(3);
    },
  },
  mounted() {
    this.fetchLeaderboard();
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    getAvatar(user) {
      return user.avatar || user.profile_image || "/assets/img/acad-icon.png";
    },
    onImgError(e) {
      e.target.src = "/assets/img/acad-icon.png";
    },
    applyFilter(filterId) {
      this.selectedFilter = filterId;
      this.fetchLeaderboard(true);
    },
    async fetchLeaderboard(isFilterChange = false) {
      this.loading = true;
      this.error = null;

      try {
        // Build query params
        const params = new URLSearchParams();
        if (this.selectedFilter && this.filterKey) {
          params.append(this.filterKey, this.selectedFilter);
        }

        const queryString = params.toString();
        const url = `${apiUrl}/qbanks/leader-board${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(this.$t("qbank.leaderboard_error") || "فشل في تحميل المتصدرين");
        }

        const data = await response.json();
        
        // Handle response
        this.leaders = data.leaders || [];
        this.userPoints = data.user_points;
        this.userRank = data.user_rank;
        
        // Only update children and filterKey on initial load (not on filter change)
        if (!isFilterChange) {
          this.children = data.children || [];
          this.filterKey = data.filter_key || "sub_category_id";
        }
      } catch (err) {
        console.error("Error fetching leaderboard:", err);
        this.error = err.message || this.$t("common.error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.qbank-leaderboard {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fffe 0%, #f0fdf4 100%);
  padding-bottom: 30px;
}

/* ========== Header ========== */
.leaderboard-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: white;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon svg {
  width: 28px;
  height: 28px;
}

.header-text h1 {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
}

.header-text p {
  font-size: 0.85rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
}

/* ========== Filter Section ========== */
.filter-section {
  padding: 16px;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filter-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.9rem;
}

.filter-header i {
  color: #00c744;
  font-size: 0.9rem;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 25px;
  background: #f8fafc;
  font-size: 0.9rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.25s ease;
  font-weight: 500;
}

.filter-chip:hover {
  border-color: #00c744;
  background: #f0fff5;
  color: #00c744;
}

.filter-chip--active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-color: #00c744;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.filter-chip--active:hover {
  background: linear-gradient(135deg, #00b33e 0%, #009635 100%);
  color: white;
}

.filter-chip i {
  font-size: 0.8rem;
}

/* ========== User Rank Card ========== */
.user-rank-card {
  margin: 15px 15px 15px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border: 2px solid #00c744;
  border-radius: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-rank-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-rank-icon {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-rank-icon i {
  color: white;
  font-size: 1.2rem;
}

.user-rank-details {
  display: flex;
  flex-direction: column;
}

.user-rank-label {
  font-size: 0.8rem;
  color: #718096;
}

.user-rank-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #00a83b;
}

.user-points-info {
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-points-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
}

.user-points-label {
  font-size: 0.8rem;
  color: #718096;
}

/* ========== Loading State ========== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.loading-spinner {
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
}

.spinner-ring {
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.spinner-ring:nth-child(1) { animation-delay: -0.32s; }
.spinner-ring:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

.loading-state p {
  color: #718096;
  font-size: 0.95rem;
}

/* ========== Error State ========== */
.error-state {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
}

.error-icon i {
  font-size: 28px;
  color: #ef4444;
}

.error-state h4 {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.error-state p {
  color: #718096;
  font-size: 0.9rem;
  margin-bottom: 20px;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.4);
}

/* ========== Empty State ========== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #00c744;
}

.empty-state h4 {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-state p {
  color: #718096;
  font-size: 0.9rem;
}

/* ========== Podium Section ========== */
.podium-section {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  padding: 30px 20px 20px;
  background: linear-gradient(180deg, rgba(0, 199, 68, 0.1) 0%, transparent 100%);
}

.podium-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.podium-first {
  order: 2;
}

.podium-second {
  order: 1;
}

.podium-third {
  order: 3;
}

.crown-icon {
  font-size: 24px;
  margin-bottom: 5px;
  animation: float 2s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.podium-avatar {
  position: relative;
  margin-bottom: 10px;
}

.podium-first .podium-avatar img {
  width: 80px;
  height: 80px;
  border: 4px solid #00c744;
}

.podium-second .podium-avatar img,
.podium-third .podium-avatar img {
  width: 65px;
  height: 65px;
  border: 3px solid #94a3b8;
}

.podium-avatar img {
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.podium-rank {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
}

.podium-first .podium-rank {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
}

.podium-second .podium-rank,
.podium-third .podium-rank {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
}

.podium-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2d3748;
  text-align: center;
  max-width: 90px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.podium-score {
  font-size: 0.85rem;
  font-weight: 600;
  color: #00a83b;
  margin-top: 4px;
}

.podium-bar {
  width: 70px;
  border-radius: 8px 8px 0 0;
  margin-top: 10px;
}

.podium-bar.first {
  height: 100px;
  background: linear-gradient(180deg, #00c744 0%, #00a83b 100%);
}

.podium-bar.second {
  height: 70px;
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.podium-bar.third {
  height: 50px;
  background: linear-gradient(180deg, #cd7f32 0%, #a0522d 100%);
}

/* ========== Leaderboard List ========== */
.leaderboard-list {
  padding: 0 15px;
}

.leaderboard-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border-radius: 14px;
  margin-bottom: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.leaderboard-item:hover {
  transform: translateX(-4px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.leaderboard-item.current-user {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border: 2px solid #00c744;
}

.item-rank {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  flex-shrink: 0;
}

.item-avatar {
  width: 45px;
  height: 45px;
  flex-shrink: 0;
}

.item-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-stats {
  font-size: 0.8rem;
  color: #718096;
  margin-top: 2px;
}

.item-score {
  font-size: 1rem;
  font-weight: 700;
  color: #00a83b;
  flex-shrink: 0;
}

/* ========== Dark Mode ========== */
[data-bs-theme="dark"] .qbank-leaderboard,
[data-theme="dark"] .qbank-leaderboard,
.dark .qbank-leaderboard {
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .leaderboard-header,
[data-theme="dark"] .leaderboard-header,
.dark .leaderboard-header {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
}

[data-bs-theme="dark"] .loading-state p,
[data-theme="dark"] .loading-state p,
.dark .loading-state p,
[data-bs-theme="dark"] .empty-state p,
[data-theme="dark"] .empty-state p,
.dark .empty-state p,
[data-bs-theme="dark"] .error-state p,
[data-theme="dark"] .error-state p,
.dark .error-state p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .empty-state h4,
[data-theme="dark"] .empty-state h4,
.dark .empty-state h4,
[data-bs-theme="dark"] .error-state h4,
[data-theme="dark"] .error-state h4,
.dark .error-state h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
}

[data-bs-theme="dark"] .podium-section,
[data-theme="dark"] .podium-section,
.dark .podium-section {
  background: linear-gradient(180deg, rgba(0, 199, 68, 0.15) 0%, transparent 100%);
}

[data-bs-theme="dark"] .podium-name,
[data-theme="dark"] .podium-name,
.dark .podium-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .leaderboard-item,
[data-theme="dark"] .leaderboard-item,
.dark .leaderboard-item {
  background: #1e293b;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .leaderboard-item.current-user,
[data-theme="dark"] .leaderboard-item.current-user,
.dark .leaderboard-item.current-user {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  border-color: #00c744;
}

[data-bs-theme="dark"] .item-name,
[data-theme="dark"] .item-name,
.dark .item-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .item-stats,
[data-theme="dark"] .item-stats,
.dark .item-stats {
  color: #94a3b8;
}

[data-bs-theme="dark"] .item-rank,
[data-theme="dark"] .item-rank,
.dark .item-rank {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: #e2e8f0;
}

[data-bs-theme="dark"] .item-avatar img,
[data-theme="dark"] .item-avatar img,
.dark .item-avatar img {
  border-color: #475569;
}

[data-bs-theme="dark"] .filter-section,
[data-theme="dark"] .filter-section,
.dark .filter-section {
  background: #1e293b;
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .filter-header,
[data-theme="dark"] .filter-header,
.dark .filter-header {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .filter-chip,
[data-theme="dark"] .filter-chip,
.dark .filter-chip {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .filter-chip:hover,
[data-theme="dark"] .filter-chip:hover,
.dark .filter-chip:hover {
  background: #0f5132;
  border-color: #00c744;
  color: #4ade80;
}

[data-bs-theme="dark"] .filter-chip--active,
[data-theme="dark"] .filter-chip--active,
.dark .filter-chip--active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-color: #00c744;
  color: white;
}

[data-bs-theme="dark"] .filter-select,
[data-theme="dark"] .filter-select,
.dark .filter-select {
  background-color: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .filter-select:focus,
[data-theme="dark"] .filter-select:focus,
.dark .filter-select:focus {
  background-color: #1e293b;
  border-color: #00c744;
}

[data-bs-theme="dark"] .user-rank-card,
[data-theme="dark"] .user-rank-card,
.dark .user-rank-card {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  border-color: #00c744;
}

[data-bs-theme="dark"] .user-rank-label,
[data-theme="dark"] .user-rank-label,
.dark .user-rank-label,
[data-bs-theme="dark"] .user-points-label,
[data-theme="dark"] .user-points-label,
.dark .user-points-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .user-rank-value,
[data-theme="dark"] .user-rank-value,
.dark .user-rank-value {
  color: #4ade80;
}

[data-bs-theme="dark"] .user-points-value,
[data-theme="dark"] .user-points-value,
.dark .user-points-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .podium-score,
[data-theme="dark"] .podium-score,
.dark .podium-score {
  color: #4ade80;
}

[data-bs-theme="dark"] .item-score,
[data-theme="dark"] .item-score,
.dark .item-score {
  color: #4ade80;
}
</style>
